import React, {CSSProperties} from "react";
import {
    Text,
    TextInput,
    Button,
    Group,
    Grid,
    Stack,
    Title,
    Divider,
    Anchor,
    PasswordInput,
    useMantineTheme,
    Textarea
} from '@mantine/core';
import {useForm} from '@mantine/form';
import MantineForm from "../../common/Mantine.form";
import MantineDropzone from "../../common/Dropzone/Dropzone";
import {PDF_MIME_TYPE} from "@mantine/dropzone";
import {useNavigate} from "react-router-dom";
import ButtonFillFormTest from "../../common/Button.fill.form.test/Button.fill.form.test";
import AgentReviewDocuments from "../Agent.review/Agent.review.documents";

interface Props {
    onSubmitQuery?: (query: any) => void,
    initialValues?: any,
    style?: CSSProperties,
    disableUpdateText?: boolean,
    customRenderFiles?: any,
    curDocuments?: any[],
    isUpdate?: boolean,
}

interface FormValues {
    agentName: string,
    address1: string,
    address2: string,
    city: string,
    zone: string,
    postalCode: string,
    countryCode: string,
    telephone1: string,
    telephone2: string,
    fax: string,
    contactName: string,
    email: string,
    url: string,
    files?: File[]
    formPassword: string,
    confirmFormPassword: string,
}

function AgentSubmission({
                             onSubmitQuery,
                             initialValues,
                             style,
                             disableUpdateText,
                             customRenderFiles,
                             curDocuments,
                             isUpdate
                         }: Props) {
    const navigate = useNavigate();
    const theme = useMantineTheme();
    const form = useForm<FormValues>({
        initialValues: initialValues || {
            agentName: '',
            address1: '',
            address2: '',
            city: '',
            zone: '',
            postalCode: '',
            countryCode: '',
            telephone1: '',
            telephone2: '',
            fax: '',
            contactName: '',
            email: '',
            url: '',
            formPassword: '',
            confirmFormPassword: "",
            files: []
        },

        validate: {
            agentName: (value) => (value.length <= 60 ? null : 'Up to 60 characters allowed.'),
            telephone1: (value) => {
                if (value) {
                    return /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im.test(value) ? null : 'Invalid Phone Number'
                }
            },
            telephone2: (value) => {
                if (value) {
                    return /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im.test(value) ? null : 'Invalid Phone Number'
                }
            },
            fax: (value) => {
                if (value) {
                    return /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im.test(value) ? null : 'Invalid Fax Number'
                }
            },
            contactName: (value) => {
                if (value) {
                    return value.length <= 30 ? null : 'Up to 30 characters allowed.'
                }
            },
            email: (value) => {
                if (value) {
                    return /^\S+@\S+$/.test(value) ? null : 'Invalid email';
                }
            },
            url: (value) => {
                if (value) {
                    return /^(ftp|http|https):\/\/[^ "]+$/.test(value) ? null : 'Invalid URL';
                }
            },

        },
    });

    function onSubmit(values: typeof form.values) {
        if (onSubmitQuery) {
            onSubmitQuery(values)
        }
    }

    function onDrop(files: File[]) {
        let curFiles = form.values.files || [];
        form.setFieldValue("files", [...curFiles, ...files]);
    }

    function onDeleteFile(fileIndex: number) {
        let curFiles = [...(form.values.files || [])];
        curFiles.splice(fileIndex, 1);
        form.setFieldValue("files", curFiles)
    }

    function onButtonFillFormTestClick() {
        form.setValues({
            agentName: 'Test User',
            address1: '123 Street',
            address2: '',
            city: 'Atlanta',
            zone: 'GA',
            postalCode: '30000',
            countryCode: 'US',
            telephone1: '1234567890',
            telephone2: '',
            fax: '',
            contactName: '',
            email: 'testuser@gmail.com',
            url: '',
            formPassword: "password123",
            confirmFormPassword: "password123",
        })
    }

    return (
        <Stack style={style}>
            <Group position="apart">
                <Title order={3}> Agent Submission Form</Title>
                {initialValues?.agentStatus &&
                    <Text>
                        {initialValues?.agentStatus}
                    </Text>}
            </Group>
            <Divider/>
            <ButtonFillFormTest onClick={onButtonFillFormTestClick}>
                Test Fill Agent Submission Form
            </ButtonFillFormTest>
            {initialValues?.comments &&
                <Textarea label="AGL Comments" readOnly
                          description="These are comments left for you by an AGL Representative">
                    {initialValues?.comments}
                </Textarea>}
            <form onSubmit={form.onSubmit(onSubmit)}>
                <Grid>
                    <Grid.Col span={12}>
                        <TextInput
                            required
                            label="Agent Name"
                            placeholder="Agent Name"
                            {...form.getInputProps('agentName')}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <TextInput
                            required
                            label="Email"
                            placeholder="agent@email.com"
                            {...form.getInputProps('email')}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <TextInput
                            label="Contact Name"
                            placeholder="Contact Name"
                            {...form.getInputProps('contactName')}
                        />
                    </Grid.Col>
                    <Grid.Col span={6}>
                        <TextInput
                            required
                            label="Telephone 1"
                            placeholder="###-###-####"
                            {...form.getInputProps('telephone1')}
                        />
                    </Grid.Col>
                    <Grid.Col span={6}>
                        <TextInput
                            label="Telephone 2"
                            placeholder="###-###-####"
                            {...form.getInputProps('telephone2')}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <TextInput
                            label="Fax"
                            placeholder="###-###-####"
                            {...form.getInputProps('fax')}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <TextInput
                            label="URL"
                            placeholder="https://www.url.com"
                            {...form.getInputProps('url')}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <MantineForm.Address form={form}/>
                    </Grid.Col>
                    {!isUpdate &&
                        <Grid.Col span={12}>
                            <PasswordInput label="Form Password"
                                           description="You will use your email above and this form password to access/update your your submission after you have submitted your information."
                                           placeholder="Form Password"
                                           required
                                           {...form.getInputProps('formPassword')}/>
                        </Grid.Col>}
                    {!isUpdate &&
                        <Grid.Col span={12}>
                            <PasswordInput label="Confirm Form Password"
                                           placeholder="Confirm Form Password"
                                           required
                                           {...form.getInputProps('confirmFormPassword')}/>
                        </Grid.Col>}
                    {curDocuments &&
                        <Grid.Col span={12}>
                            <Text size="sm" mb={6}>Previously Uploaded Files</Text>
                            <AgentReviewDocuments agentDocuments={curDocuments}></AgentReviewDocuments>
                        </Grid.Col>
                    }
                    <Grid.Col span={12}>
                        <Text color="gray" size="sm" mb={6}>Files</Text>
                        <MantineDropzone
                            onDeleteFile={onDeleteFile}
                            maxSize={3 * 1024 ** 2}
                            accept={PDF_MIME_TYPE}
                            onDrop={onDrop}
                            files={form.values.files}
                            customRenderFiles={customRenderFiles}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <Group position={disableUpdateText ? "right" : "apart"} mt="md">
                            {!disableUpdateText &&
                                <Anchor
                                    component="button"
                                    type="button"
                                    color="gray"
                                    onClick={() => navigate("/agent/update")}
                                    size="sm"
                                >
                                    Submitted an application? Update it here.
                                </Anchor>}
                            <Button type="submit">Submit</Button>
                        </Group>
                    </Grid.Col>
                </Grid>
            </form>
        </Stack>

    );
}

export default AgentSubmission;